import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Calendar, Trash2, Plus, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_COLORS = {
  holiday: 'bg-rose-100 text-rose-700 border-rose-300',
  event:   'bg-blue-100 text-blue-700 border-blue-300',
  exam:    'bg-amber-100 text-amber-700 border-amber-300',
};

/**
 * Holiday Calendar widget.
 * - role='head_teacher' shows add/delete/sync controls.
 * - role anything else is read-only.
 */
const HolidayCalendar = ({ role = 'student' }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date: '', title: '', type: 'holiday', description: '' });
  const [published, setPublished] = useState(false);
  const [togglingPublish, setTogglingPublish] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [holidaysRes, pubRes] = await Promise.all([
        axios.get(`${API}/holidays`, { params: { upcoming_only: true, limit: 50 } }),
        axios.get(`${API}/me/calendar-published`).catch(() => ({ data: { published: false } })),
      ]);
      setItems(holidaysRes.data || []);
      setPublished(Boolean(pubRes.data?.published));
    } catch (e) {
      // silent
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const togglePublish = async () => {
    setTogglingPublish(true);
    try {
      const res = await axios.put(`${API}/head-teacher/calendar-published`, { published: !published });
      setPublished(Boolean(res.data?.published));
      toast.success(res.data?.published ? 'Academic calendar published to students & teachers' : 'Calendar unpublished — hidden from students & teachers');
    } catch (e) {
      toast.error('Failed to update publish state');
    } finally {
      setTogglingPublish(false);
    }
  };

  const add = async () => {
    if (!form.date || !form.title) {
      toast.error('Please fill date and title');
      return;
    }
    setAdding(true);
    try {
      await axios.post(`${API}/head-teacher/holidays`, form);
      toast.success('Added');
      setShowForm(false);
      setForm({ date: '', title: '', type: 'holiday', description: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setAdding(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Remove this entry?')) return;
    try {
      await axios.delete(`${API}/head-teacher/holidays/${id}`);
      load();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const syncIndia = async () => {
    const year = new Date().getFullYear();
    if (!window.confirm(`Import the ${year} Indian public holiday calendar? You can still edit or remove any entry afterwards.`)) return;
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/head-teacher/holidays/sync-india`, { year });
      const { added, skipped } = res.data || {};
      toast.success(`Imported ${added} holiday${added === 1 ? '' : 's'}${skipped ? ` (${skipped} already present)` : ''}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally { setSyncing(false); }
  };

  if (loading) return null;

  // Non-HT viewers only see the calendar when HT has explicitly published it.
  if (role !== 'head_teacher' && !published) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="holiday-calendar">
      <div className="px-4 py-3 bg-gradient-to-r from-rose-500 to-pink-500 text-white flex items-center justify-between gap-2">
        <h3 className="font-semibold flex items-center gap-2 text-sm">
          <Calendar size={18} /> Upcoming Holidays &amp; Events
        </h3>
        {role === 'head_teacher' && (
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={togglePublish}
              disabled={togglingPublish}
              className={`h-7 text-xs ${published ? 'bg-white text-rose-600 hover:bg-white' : 'text-white hover:bg-white/20 border border-white/40'}`}
              data-testid="toggle-publish-calendar-btn"
              title={published ? 'Calendar is visible to students & teachers — click to hide' : 'Calendar is hidden from students & teachers — click to publish'}
            >
              {togglingPublish ? <Loader2 size={12} className="animate-spin mr-1" /> : <Sparkles size={12} className="mr-1" />}
              {published ? 'Published' : 'Publish to school'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={syncIndia}
              disabled={syncing}
              className="text-white hover:bg-white/20 h-7 text-xs"
              data-testid="sync-india-holidays-btn"
              title="Auto-import official Indian public holidays for this year"
            >
              {syncing ? <Loader2 size={12} className="animate-spin mr-1" /> : <Sparkles size={12} className="mr-1" />}
              Sync India
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowForm(s => !s)} className="text-white hover:bg-white/20 h-7" data-testid="add-holiday-btn">
              <Plus size={14} className="mr-1" /> Add
            </Button>
          </div>
        )}
      </div>

      {role === 'head_teacher' && !published && (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-700 flex items-center gap-2">
          <strong>Draft mode.</strong> Students & teachers won't see this calendar until you click "Publish to school".
        </div>
      )}

      {showForm && role === 'head_teacher' && (
        <div className="p-3 border-b bg-rose-50/40 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <input
              type="date"
              value={form.date}
              onChange={e => setForm({ ...form, date: e.target.value })}
              className="border rounded px-2 py-1.5 text-sm"
              data-testid="holiday-date"
            />
            <select
              value={form.type}
              onChange={e => setForm({ ...form, type: e.target.value })}
              className="border rounded px-2 py-1.5 text-sm bg-white"
              data-testid="holiday-type"
            >
              <option value="holiday">Holiday</option>
              <option value="event">Event</option>
              <option value="exam">Exam</option>
            </select>
          </div>
          <input
            type="text"
            value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })}
            placeholder="Title (e.g. Independence Day)"
            className="border rounded px-2 py-1.5 text-sm w-full"
            data-testid="holiday-title"
          />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setShowForm(false)} className="h-7 text-xs">Cancel</Button>
            <Button size="sm" onClick={add} disabled={adding} className="bg-rose-600 hover:bg-rose-700 h-7 text-xs" data-testid="save-holiday-btn">
              {adding ? <Loader2 size={12} className="animate-spin" /> : 'Save'}
            </Button>
          </div>
        </div>
      )}

      <div className="max-h-56 overflow-y-auto">
        {items.length === 0 ? (
          <div className="p-4 text-center text-xs text-gray-400">No upcoming holidays.</div>
        ) : (
          <ul className="divide-y">
            {items.slice(0, 15).map(h => {
              const d = new Date(h.date);
              return (
                <li key={h.id} className="px-3 py-2 flex items-center gap-2 group" data-testid={`holiday-${h.id}`}>
                  <div className="text-center w-11 shrink-0">
                    <p className="text-[10px] uppercase text-gray-500 font-semibold">{d.toLocaleDateString('en-IN', { month: 'short' })}</p>
                    <p className="text-lg font-bold text-rose-600 leading-none">{d.getDate()}</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{h.title}</p>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${TYPE_COLORS[h.type] || ''}`}>
                      {h.type}
                    </span>
                  </div>
                  {role === 'head_teacher' && (
                    <button onClick={() => del(h.id)} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500" data-testid={`delete-holiday-${h.id}`}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default HolidayCalendar;
